// frontend/src/pages/PortalNotificationsPage.jsx
//
// Buzón del canal "portal": aquí se leen los reportes que el sistema registró
// como entregados en el portal.
//
// Antes esto no existía: la auditoría decía «Enviado · Disponible en portal y
// bitácora» y no había ninguna pantalla donde verlo. El contenido siempre estuvo
// guardado; lo que faltaba era la puerta para abrirlo.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, List, ListItemButton, ListItemText, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  MarkEmailUnread as UnreadIcon,
  NotificationsActive as BellIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../services/api';

const fmt = (value) => (value ? new Date(value).toLocaleString('es-MX') : '—');

// La campana del navbar mantiene su propio contador. Sin este aviso, leer una
// notificación aquí no la enteraba y el número se quedaba pegado hasta recargar.
export const PORTAL_NOTIFICATIONS_READ_EVENT = 'aps-portal-notifications-read';
const avisarLeidas = () => window.dispatchEvent(new CustomEvent(PORTAL_NOTIFICATIONS_READ_EVENT));

const PortalNotificationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.portalNotifications.list()
      .then((res) => setItems(res.data?.items || []))
      .catch((err) => setError(err?.response?.data?.detail || 'No se pudieron cargar las notificaciones'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);


  const open = useCallback((id) => {
    setDetailLoading(true);
    setDetail({ id });
    api.portalNotifications.detail(id)
      .then((res) => setDetail(res.data))
      // Abrir marca como leído en el backend, así que la lista se refresca.
      .then(() => { load(); avisarLeidas(); })
      .catch((err) => setDetail({ id, _error: err?.response?.data?.detail || 'No se pudo abrir' }))
      .finally(() => setDetailLoading(false));
  }, [load]);

  // Al pulsar una notificación en la campana se llega aquí con `?open=<id>`:
  // se abre sola, que es lo que esperaba quien la pulsó. El parámetro se retira
  // para que recargar o volver atrás no la reabra.
  useEffect(() => {
    const id = searchParams.get('open');
    if (!id) return;
    open(id);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, open]);

  const markAll = () => {
    api.portalNotifications.markAllRead()
      .then(() => { load(); avisarLeidas(); })
      .catch(() => {});
  };

  const unread = items.filter((item) => !item.is_read).length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <BellIcon color="primary" />
            <Typography variant="h5" fontWeight={800}>Mis notificaciones</Typography>
            {unread > 0 && <Chip size="small" color="error" label={`${unread} sin leer`} />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Reportes entregados en el portal para tu empresa activa.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {unread > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={markAll}>
              Marcar todas leídas
            </Button>
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Actualizar</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {!loading && items.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Todavía no hay notificaciones entregadas en el portal para esta empresa.
          </Typography>
        </Paper>
      )}

      {!loading && items.length > 0 && (
        <Paper variant="outlined">
          <List disablePadding>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <Divider component="li" />}
                <ListItemButton onClick={() => open(item.id)} sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {!item.is_read && (
                          <Tooltip title="Sin leer"><UnreadIcon color="error" fontSize="small" /></Tooltip>
                        )}
                        <Typography variant="subtitle2" fontWeight={item.is_read ? 500 : 800}>
                          {item.subject}
                        </Typography>
                        <Chip size="small" variant="outlined" label={item.event_label} />
                      </Stack>
                    }
                    secondary={`Recibido ${fmt(item.sent_at)}${item.is_read ? ` · leído ${fmt(item.read_at)}` : ''}`}
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 0 }} noWrap>
            {detail?.subject || 'Notificación'}
          </Typography>
          <IconButton onClick={() => setDetail(null)} aria-label="Cerrar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {detailLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}
          {!detailLoading && detail?._error && <Alert severity="error" sx={{ m: 2 }}>{detail._error}</Alert>}
          {!detailLoading && detail?.html && (
            // Se renderiza aislado en un iframe: el HTML es el mismo del correo,
            // con sus propios estilos, y no debe heredar ni contaminar el tema.
            <iframe
              title={detail.subject || 'Notificación'}
              srcDoc={detail.html}
              sandbox=""
              style={{ width: '100%', height: '70vh', border: 0, display: 'block' }}
            />
          )}
          {!detailLoading && detail && !detail.html && !detail._error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="info" sx={{ mb: detail.ticket_id ? 2 : 0 }}>
                {detail.is_report === false
                  ? 'Esta notificación corresponde a una alarma, no a un reporte. El detalle completo está en su ticket.'
                  : 'Esta notificación se registró antes de que el portal guardara el contenido, así que no hay nada que mostrar.'}
              </Alert>
              {detail.ticket_id && (
                <Button variant="contained" size="small"
                  onClick={() => { setDetail(null); navigate(`/alarm-tickets/${detail.ticket_id}`); }}>
                  Abrir ticket
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PortalNotificationsPage;
